import React, { useState, useEffect } from 'react';
import { Button, Modal } from '../../components';
import { getUsers, adminDeleteUser, UserProfile } from '../../services/user-service';

const getRoleColor = (role: string) => {
  switch (role) {
    case 'admin':    return 'bg-role-admin/20 text-role-admin';
    case 'mentor':   return 'bg-role-mentor/20 text-role-mentor';
    case 'founder':  return 'bg-role-founder/20 text-role-founder';
    case 'investor': return 'bg-role-investor/20 text-role-investor';
    default:         return 'bg-muted/20 text-muted';
  }
};

const Users: React.FC = () => {
  const [users, setUsers]             = useState<UserProfile[]>([]);
  const [loading, setLoading]         = useState(true);
  const [fetchError, setFetchError]   = useState('');
  const [selectedRole, setSelectedRole]     = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [searchTerm, setSearchTerm]         = useState('');
  const [deleteTarget, setDeleteTarget]     = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting]             = useState(false);

  useEffect(() => {
    getUsers()
      .then(setUsers)
      .catch(e => setFetchError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const filteredUsers = users.filter(u => {
    const roleMatch   = selectedRole === 'All' || u.role === selectedRole.toLowerCase();
    const searchMatch = u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        u.email.toLowerCase().includes(searchTerm.toLowerCase());
    return roleMatch && searchMatch;
  });

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminDeleteUser(deleteTarget.id);
      setUsers(prev => prev.filter(u => u._id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (e: any) {
      alert(e.message);
    } finally {
      setDeleting(false);
    }
  };

  const stats = [
    { label: 'Total Users', value: users.length },
    { label: 'Founders', value: users.filter(u => u.role === 'founder').length },
    { label: 'Mentors', value: users.filter(u => u.role === 'mentor').length },
    { label: 'Admins', value: users.filter(u => u.role === 'admin').length },
  ];

  return (
    <div className="section-gap">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-text">User Management</h1>
          <p className="text-muted">Manage users and their roles</p>
        </div>
      </div>

      {fetchError && (
        <div className="p-3 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm">{fetchError}</div>
      )}

      {/* Stats */}
      <div className="flex flex-wrap gap-x-8 pb-6 border-b border-line">
        {stats.map((s, i, arr) => (
          <div key={s.label} className={`flex flex-col ${i < arr.length - 1 ? 'pr-8 border-r border-line' : ''}`}>
            <span className="text-2xl font-bold text-text">{s.value}</span>
            <span className="text-xs text-muted mt-0.5">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center py-4 border-b border-line">
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by name or email…"
          className="bg-surface border border-line rounded-lg px-3 py-2 text-text text-sm w-64 placeholder:text-muted"
        />
        <select
          value={selectedRole}
          onChange={e => setSelectedRole(e.target.value)}
          className="bg-surface border border-line rounded-lg px-3 py-2 text-text text-sm"
        >
          <option value="All">All Roles</option>
          <option value="Founder">Founder</option>
          <option value="Mentor">Mentor</option>
          <option value="Admin">Admin</option>
        </select>
        <select
          value={selectedStatus}
          onChange={e => setSelectedStatus(e.target.value)}
          className="bg-surface border border-line rounded-lg px-3 py-2 text-text text-sm"
        >
          <option value="All">All Status</option>
          <option value="Active">Active</option>
        </select>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-chart border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted uppercase tracking-wider">
                <th className="py-3 text-left font-semibold border-b border-line">Name</th>
                <th className="py-3 text-left font-semibold border-b border-line">Role</th>
                <th className="py-3 text-left font-semibold border-b border-line">Company</th>
                <th className="py-3 text-left font-semibold border-b border-line">Title</th>
                <th className="py-3 text-left font-semibold border-b border-line">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user._id} className="border-b border-line last:border-0 hover:bg-surface/30 transition-colors">
                  <td className="py-4 pr-4">
                    <div className="flex items-center gap-3">
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-chart/20 flex items-center justify-center text-chart font-bold text-sm">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="text-text font-medium">{user.name}</div>
                        <div className="text-muted text-xs">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 pr-4">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="py-4 pr-4 text-muted text-sm">{user.company || '—'}</td>
                  <td className="py-4 pr-4 text-muted text-sm">{user.title || '—'}</td>
                  <td className="py-4">
                    <Button variant="ghost" size="sm"
                      onClick={() => setDeleteTarget({ id: user._id, name: user.name })}
                      className="text-danger hover:text-danger/70">
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <p className="text-muted text-sm py-6">No users found. Try adjusting your search or filters.</p>
          )}
          <p className="text-muted text-xs mt-4">Showing {filteredUsers.length} of {users.length} users</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove User">
        <p className="text-sm text-muted mb-6">
          Remove <span className="text-text font-medium">{deleteTarget?.name}</span>? Their account will be
          disabled and they won't be able to log in. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmDelete}
            disabled={deleting}
            className="!bg-danger hover:!bg-danger/90"
          >
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Users;
